import React from 'react';
import { GetStaticProps } from 'next';
import Head from 'next/head';
import Link from 'next/link';
import client from '../lib/apollo';
import { GET_POSTS, GET_SITE_INFO } from '../lib/queries';
import Image from 'next/image';
import Header from '../components/Header';

interface Post {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  date: string;
  featuredImage?: {
    node: {
      sourceUrl: string;
      altText: string;
    };
  };
  author: {
    node: {
      name: string;
      slug: string;
    };
  };
  categories: {
    nodes: Array<{
      name: string;
      slug: string;
    }>;
  };
}

interface SiteInfo {
  title: string;
  description: string;
  url: string;
}

interface BlogPageProps {
  posts: Post[];
  siteInfo: SiteInfo;
}

export default function BlogPage({ posts, siteInfo }: BlogPageProps) {
  const phoneNumber = "(630) 715-8829";
  const companyName = "Partners Plumbing Co.";
  const serviceArea = "Kane County, Illinois";
  const address = "Kane County, IL 60177";
  const email = "info@partnersinplumbing.com";

  return (
    <>
      <Head>
        <title>{`Plumbing Blog - ${companyName} | Tips, Advice & Industry News`}</title>
        <meta name="description" content={`Expert plumbing advice, maintenance tips, and industry insights from ${companyName}. Learn how to maintain your plumbing system and when to call professionals.`} />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link rel="canonical" href="https://partnersinplumbing.com/blog" />
        <link rel="icon" href="/favicon.ico" />
      </Head>

      <div className="min-h-screen bg-white font-inter">
        <Header currentPage="/blog" phoneNumber={phoneNumber} companyName={companyName} />

        {/* Hero Section */}
        <section className="bg-gradient-to-br from-slate-800 via-slate-900 to-slate-900 text-white py-12 md:py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              <div className="text-center lg:text-left">
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 lg:mb-6 leading-tight">Plumbing Tips & Insights</h1>
                <p className="text-lg sm:text-xl mb-6 lg:mb-8 text-slate-100 leading-relaxed">
                  Expert advice, maintenance tips, and industry insights from our professional plumbers serving Kane County, Illinois.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <a href={`tel:${phoneNumber}`} className="bg-yellow-500 text-slate-900 px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold text-base sm:text-lg hover:bg-yellow-400 transition-all duration-200 shadow-lg hover:shadow-xl text-center">
                    Call {phoneNumber}
                  </a>
                  <Link href="/contact" className="bg-transparent border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-bold text-base sm:text-lg hover:bg-white hover:text-slate-900 transition-all duration-200 text-center">
                    Schedule Service
                  </Link>
                </div>
              </div>
              <div className="hidden lg:block">
                <div className="bg-white/10 p-8 rounded-xl backdrop-blur-sm border border-white/20 shadow-2xl">
                  <h3 className="text-2xl font-bold mb-4">Expert Knowledge</h3>
                  <p className="text-slate-100 mb-4 leading-relaxed">Professional plumbing advice and maintenance tips from licensed experts</p>
                  <div className="flex items-center text-yellow-400">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center mr-3">
                      <div className="w-4 h-4 bg-yellow-600 rounded-full"></div>
                    </div>
                    <span className="font-semibold">Free Resources</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WordPress Blog Posts */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">Latest Posts</h2>
              <p className="text-xl text-gray-600">Recent updates from our plumbing experts</p>
            </div>
            
            {posts.length > 0 ? (
              <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
                {posts.map((post) => (
                  <article key={post.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
                    {post.featuredImage && (
                      <div className="aspect-w-16 aspect-h-9">
                        <img
                          src={post.featuredImage.node.sourceUrl}
                          alt={post.featuredImage.node.altText || post.title}
                          className="w-full h-48 object-cover"
                        />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
                        <time dateTime={post.date}>
                          {new Date(post.date).toLocaleDateString()}
                        </time>
                        {post.categories.nodes.length > 0 && (
                          <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded">
                            {post.categories.nodes[0].name}
                          </span>
                        )}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">
                        <Link href={`/posts/${post.slug}`} className="hover:text-slate-900 transition-colors">
                          {post.title}
                        </Link>
                      </h3>
                      <div className="text-gray-600 mb-4 line-clamp-3" dangerouslySetInnerHTML={{ __html: post.excerpt }} />
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-500">By {post.author.node.name}</span>
                        <Link href={`/posts/${post.slug}`} className="text-slate-800 hover:text-slate-900 font-medium">
                          Read More →
                        </Link>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Posts Available</h3>
                <p className="text-gray-600 mb-6">We're working on adding new content. Check back soon!</p>
                <Link href="/contact" className="bg-slate-800 text-white px-6 py-3 rounded-lg font-semibold hover:bg-slate-900 transition-colors">
                  Contact Us
                </Link>
              </div>
            )}
          </div>
        </section>

        {/* Newsletter Signup */}
        <section className="py-16 bg-slate-800 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-6">Stay Informed</h2>
            <p className="text-xl mb-8 text-slate-100 max-w-2xl mx-auto">
              Get the latest plumbing tips, maintenance reminders, and special offers delivered to your inbox
            </p>
            <div className="max-w-md mx-auto">
              <div className="flex gap-4">
                <input
                  type="email"
                  placeholder="Enter your email"
                  className="flex-1 px-4 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-400"
                />
                <button className="bg-yellow-500 text-slate-900 px-6 py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-colors">
                  Subscribe
                </button>
              </div>
              <p className="text-sm text-slate-100 mt-3">No spam, unsubscribe anytime</p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Need Professional Help?</h2>
            <p className="text-xl text-gray-600 mb-8">
              While our blog provides helpful tips, some plumbing issues require professional expertise
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${phoneNumber}`} className="bg-slate-800 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-slate-900 transition-colors">
                Call {phoneNumber}
              </a>
              <Link href="/contact" className="bg-transparent border-2 border-slate-800 text-slate-800 px-8 py-4 rounded-lg font-bold text-lg hover:bg-slate-900 hover:text-white transition-colors">
                Schedule Service
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-800 text-white py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-xl font-bold mb-4">{companyName}</h3>
                <p className="text-gray-300 mb-4">Professional plumbing services you can trust. Licensed, insured, and reliable.</p>
                <a href={`tel:${phoneNumber}`} className="text-yellow-400 font-bold text-lg hover:text-yellow-300 block mb-2">
                  {phoneNumber}
                </a>
                <a href={`mailto:${email}`} className="text-gray-300 hover:text-white block mb-2">
                  {email}
                </a>
                <p className="text-gray-300 text-sm">
                  {address}
                </p>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-4">Services</h4>
                <ul className="space-y-2 text-gray-300">
                  <li><Link href="/services" className="hover:text-white">Emergency Plumbing</Link></li>
                  <li><Link href="/services" className="hover:text-white">Drain Cleaning</Link></li>
                  <li><Link href="/services" className="hover:text-white">Water Heaters</Link></li>
                  <li><Link href="/services" className="hover:text-white">Leak Detection</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-lg font-semibold mb-4">Company</h4>
                <ul className="space-y-2 text-gray-300">
                  <li><Link href="/about" className="hover:text-white">About Us</Link></li>
                  <li><Link href="/blog" className="hover:text-white">Blog</Link></li>
                  <li><Link href="/contact" className="hover:text-white">Contact</Link></li>
                  <li><Link href="/services" className="hover:text-white">Services</Link></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-700 mt-8 pt-8 text-center text-gray-400">
              <p>&copy; 2024 {companyName}. All rights reserved. | Licensed & Insured | Emergency Service Available</p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}

export const getStaticProps: GetStaticProps = async () => {
  try {
    const [postsResult, siteInfoResult] = await Promise.all([
      client.query({
        query: GET_POSTS,
        variables: { first: 9 },
      }),
      client.query({
        query: GET_SITE_INFO,
      }),
    ]);

    return {
      props: {
        posts: postsResult.data.posts.nodes || [],
        siteInfo: siteInfoResult.data.generalSettings || {
          title: 'Partners Plumbing Co.',
          description: 'Professional plumbing services you can trust',
          url: '',
        },
      },
      revalidate: 30, // Reduced from 60 seconds for faster updates
    };
  } catch (error) {
    console.error('Error fetching blog data:', error);
    
    return {
      props: {
        posts: [],
        siteInfo: {
          title: 'Partners Plumbing Co.',
          description: 'Professional plumbing services you can trust',
          url: '',
        },
      },
      revalidate: 30, // Reduced from 60 seconds for faster updates
    };
  }
}; 